<template>
    <div class="thread-users-container">
        <h2>{{ threadTitle }}</h2>

        <h3>{{ $t('messages.members.title') }}</h3>

        <ThreadUsersTableList
            ref="threadUsersTableList"
            :records="threadUsers"
            :label-key="'name'"
            :is-app-support="currentThread?.is_support_thread"
            @view="handleViewUser"
            @message="handleMessageUser"
            @delete="handleRemoveUser"
        />

        <div v-if="!exitedThread" class="d-flex justify-content-end">
            <button class="btn btn-primary" @click="showAddUser = true">
                {{ $t('messages.members.addMember') }}
                <i class="fi fi-add"></i>
            </button>
        </div>

        <form
            v-if="showAddUser"
            @submit.prevent="handleAddUser"
            class="d-flex flex-column gap-2"
        >
            <Field
                :label="$t('messages.members.selectMember.label')"
                type="multi-select"
                :key="unSelectedUsers?.length ?? -1"
                :params="{
                    options: unSelectedUsers,
                    placeholder: $t(
                        'messages.members.selectMember.placeholder'
                    ),
                }"
                v-model="selectedUsers"
            />
            <div class="d-flex justify-content-end" style="gap: 0.5rem">
                <button
                    type="button"
                    class="btn btn-white"
                    @click="showAddUser = false"
                >
                    {{ $t('common.actions.cancel') }}
                </button>
                <button type="submit" class="btn btn-secondary">
                    {{ $t('common.actions.save') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import Field from '@/components/Field.vue';
import ThreadUsersTableList from '@/components/messaging/ThreadUsersTableList.vue';
import { useMessengerStore } from '@/stores/messenger';
import { getThreadUserDisplayUser } from '@/utils/messaging';

export default {
    components: {
        Field,
        ThreadUsersTableList,
    },
    props: {
        threadId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            showAddUser: false,
            allUsers: [],
            selectedUsers: [],
            currentThread: null,
            messengerStore: null,
        };
    },
    computed: {
        exitedThread() {
            const threadUser = this.currentThread?.users.find((threadUser) => {
                return threadUser.user.id == this.$userStore.user.id;
            });
            return threadUser?.exited_at;
        },
        threadUsers() {
            if (this.currentThread) {
                let filterUsers = this.currentThread.users;

                if (this.currentThread.is_support_thread) {
                    filterUsers = filterUsers.filter(
                        (user) => !user.user.is_app_support
                    );
                }

                const mutatedUsers = filterUsers.map((threadUser) => {
                    const displayUser = getThreadUserDisplayUser(
                        threadUser,
                        this.$employeesStore
                    );

                    return {
                        ...displayUser,
                        name: [displayUser.first_name, displayUser.last_name]
                            .filter(Boolean)
                            .join(' '),
                        isExited: !!threadUser.exited_at,
                    };
                });

                mutatedUsers.sort((a, b) => {
                    if (a.isExited === b.isExited) return 0;
                    return a.isExited ? 1 : -1;
                });

                return mutatedUsers;
            }
            return [];
        },
        threadTitle() {
            return (
                this.currentThread?.subject ||
                this.$t('messages.thread.fallbackTitle')
            );
        },
        unSelectedUsers() {
            return this.allUsers.filter((user) => {
                return !this.threadUsers.some(
                    (threadUser) => threadUser.id == user.value
                );
            });
        },
    },
    methods: {
        fetchCurrentThreadUsers() {
            const payload = {
                where: {
                    id: this.threadId,
                },
                include: {
                    users: {
                        include: {
                            user: true,
                        },
                    },
                },
            };
            this.$getData('message-thread/read', payload).then((response) => {
                if (response?.success && response?.data?.length > 0) {
                    this.currentThread = response.data[0];
                }
            });
        },
        handleViewUser(user) {
            if (user?.hash) {
                this.$router.push({
                    name: 'Employee Detail',
                    params: {
                        hash: user?.hash,
                    },
                });
                this.messengerStore.closePanel();
            }
        },
        handleMessageUser(user) {
            if (user.id) {
                this.messengerStore.openNew([user.id]);
            }
        },
        handleRemoveUser(user) {
            if (user.id) {
                this.$postData('message-thread/user/remove', {
                    thread_id: this.threadId,
                    user_id: user.id,
                }).then((response) => {
                    if (response?.success) {
                        if (this.$refs.threadUsersTableList) {
                            this.$refs.threadUsersTableList?.closeModal();
                        }
                        if (this.$userStore.user.id == user.id) {
                            this.$alertStore.pushSuccess(
                                this.$t('messages.alerts.leaveSuccess')
                            );
                            this.messengerStore.openHistory();
                            return;
                        }
                        this.$alertStore.pushSuccess(
                            this.$t('messages.alerts.removeSuccess')
                        );
                        this.fetchCurrentThreadUsers();
                    } else {
                        this.$alertStore.pushError(
                            response?.message ||
                                this.$t('messages.alerts.removeError')
                        );
                    }
                });
            }
        },
        handleAddUser() {
            if (this.selectedUsers.length > 0) {
                this.$postData('message-thread/user/add', {
                    thread_id: this.threadId,
                    user_ids: this.selectedUsers.map((userId) =>
                        parseInt(userId)
                    ),
                }).then((response) => {
                    if (response?.success) {
                        this.showAddUser = false;
                        this.selectedUsers = [];
                        this.$alertStore.pushSuccess(
                            this.$t('messages.alerts.addSuccess')
                        );
                        this.fetchCurrentThreadUsers();
                    } else {
                        this.$alertStore.pushError(
                            response?.message ||
                                this.$t('messages.alerts.addError')
                        );
                    }
                });
            }
        },
    },
    mounted() {
        this.messengerStore = useMessengerStore();
        this.fetchCurrentThreadUsers();
        this.$userStore.sync(() => {
            this.$employeesStore.sync(() => {
                // load with all users
                this.allUsers = this.$employeesStore.getEmployeeOptions();
            });
        });
    },
};
</script>

<style scoped>
.thread-users-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
}
</style>
